import { useState, useEffect } from "react";
import { Modal, Form, Input, Typography, message, Select } from "antd";
import infraestructuraService from "../../services/infraestructura/infraestructuraService";

const { Title, Text } = Typography;

export default function CarpetaFormModal({
    isOpen,
    onClose,
    onSuccess,
    carpeta,
    carpetas,
    currentParent,
}) {
    const [form] = Form.useForm();
    const [permisos, setPermisos] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isFormReady, setIsFormReady] = useState(false);

    const isEditing = !!carpeta;

    // Solución para el error del form
    useEffect(() => {
        if (isOpen) {
            // Pequeño delay para asegurar que el Form está montado
            const timer = setTimeout(() => {
                setIsFormReady(true);
            }, 0);

            return () => {
                clearTimeout(timer);
                setIsFormReady(false);
            };
        }
    }, [isOpen]);

    useEffect(() => {
        if (isFormReady && isOpen) {
            if (carpeta) {
                form.setFieldsValue({
                    nombre: carpeta.nombre,
                    enlace: carpeta.enlace || "",
                    linkeado: carpeta.linkeado || 2,
                });
                setPermisos(carpeta.permisos || []);
            } else {
                form.setFieldsValue({
                    nombre: "",
                    enlace: "",
                    linkeado: 2,
                });
                setPermisos([]);
            }
        }
    }, [carpeta, isOpen, form, isFormReady]);

    // Reset form cuando se cierra el modal
    useEffect(() => {
        if (!isOpen) {
            form.resetFields();
            setPermisos([]);
            setIsFormReady(false);
        }
    }, [isOpen, form]);

    const handleSubmit = async (values) => {
        setIsLoading(true);
        try {
            const dataToSend = {
                ...values,
                permisos,
                tipo: currentParent ? "subcarpeta" : "carpeta",
                parent_id: currentParent?.oid || null,
                ruta: currentParent
                    ? `${currentParent.ruta || ""}/${values.nombre}`.replace("//", "/")
                    : `/${values.nombre}`,
                orden: Date.now(),
            };

            if (isEditing && carpeta?.oid) {
                const updated = await infraestructuraService.updateCarpeta(
                    carpeta.oid,
                    dataToSend
                );
                message.success("Carpeta actualizada correctamente");
                onSuccess({
                    isEditing: true,
                    data: {
                        ...carpeta,
                        ...dataToSend,
                        ...updated,
                        subcarpetas: carpeta.subcarpetas || [],
                    },
                });
            } else {
                const created = await infraestructuraService.createCarpeta(dataToSend);
                message.success("Carpeta creada correctamente");
                onSuccess({ isEditing: false, data: { ...created, subcarpetas: [] } });
            }

            onClose();
        } catch (error) {
            console.error("Error al guardar carpeta:", error);
            message.error("Hubo un error al guardar la carpeta");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            open={isOpen}
            onCancel={onClose}
            onOk={() => form.submit()}
            okText={isEditing ? "Actualizar" : "Crear"}
            cancelText="Cancelar"
            confirmLoading={isLoading}
            width={500}
            destroyOnHidden
            forceRender // Fuerza el renderizado inicial del Form
        >
            <Title level={4} style={{ marginBottom: 8 }}>
                {isEditing ? "Editar Carpeta" : `Nueva ${currentParent ? "Subcarpeta" : "Carpeta"}`}
            </Title>

            <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
                {isEditing
                    ? "Modifica la información de la carpeta"
                    : `Completa los datos para crear una nueva ${
                          currentParent ? "subcarpeta" : "carpeta"
                      }`}
                {currentParent && (
                    <span style={{ color: "#1890ff", display: "block", marginTop: 4 }}>
                        Se creará dentro de: {currentParent.nombre}
                    </span>
                )}
            </Text>

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                preserve={false} // Ayuda a limpiar el estado del form
            >
                <Form.Item
                    label="Nombre *"
                    name="nombre"
                    rules={[{ required: true, message: "El nombre es requerido" }]}
                >
                    <Input
                        placeholder={`Nombre de la ${
                            currentParent ? "subcarpeta" : "carpeta"
                        }`}
                    />
                </Form.Item>

                <Form.Item label="Enlace (opcional)" name="enlace">
                    <Input placeholder="https://ejemplo.com" />
                </Form.Item>

                <Form.Item
                    label="¿Carpeta Linkeada?"
                    name="linkeado"
                    rules={[{ required: true, message: "Este campo es requerido" }]}
                >
                    <Select placeholder="Selecciona una opción">
                        <Select.Option value={1}>Sí (Linkeada)</Select.Option>
                        <Select.Option value={2}>No (Normal)</Select.Option>
                    </Select>
                </Form.Item>
            </Form>
        </Modal>
    );
}